/**
 * ReadinessDashboard.jsx — LoopManager institution readiness.
 * Shaped by: G2a; owner 2026-10-12.
 * Invariants: access is RequireLauncherApp appId="loopmanager". Institution
 *   scoping stays on the caller's institution id. users.departmentId is not access.
 * Related: appCatalog.js FLAG_APP_IDS, InstitutionReportsPage.jsx.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  Collapse,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { format, formatDistanceToNow } from "date-fns";
import { Link as RouterLink } from "react-router-dom";
import Loading from "components/Loading";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";
import RequireLauncherApp from "components/appLauncher/RequireLauncherApp";
import { FDE_ROLE_LABELS, listTciRegistry } from "services/fde/fdeRegistryService";
import {
  LOOP_HEALTH_STATUS_CHIP,
  READINESS_STATUS_COLORS,
  getInstitutionReadiness,
  listLoopHealthByTciForInstitution,
  readinessStripLabel,
  getLatestQuarterlySummary,
} from "services/fde/readinessService";

const ReadinessDashboardContent = () => {
  const { userData, organizationId } = useAuth();
  const institutionId =
    userData?.activeInstitutionId ||
    userData?.institutionId ||
    organizationId ||
    null;

  const [readiness, setReadiness] = useState(null);
  const [quarterlySummary, setQuarterlySummary] = useState(null);
  const [loopHealthByTci, setLoopHealthByTci] = useState({});
  const [tciNameById, setTciNameById] = useState({});
  const [expandedTci, setExpandedTci] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    if (!institutionId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrorMessage("");
    try {
      const [row, tcis, summary, loopHealth] = await Promise.all([
        getInstitutionReadiness(institutionId),
        listTciRegistry({ institutionId }),
        getLatestQuarterlySummary(institutionId),
        listLoopHealthByTciForInstitution(institutionId).catch((err) => {
          console.warn("[ReadinessDashboard] loopHealthMetrics read skipped", err);
          return {};
        }),
      ]);
      setReadiness(row);
      setQuarterlySummary(summary);
      setLoopHealthByTci(loopHealth);
      setTciNameById(Object.fromEntries(tcis.map((tci) => [tci.id, tci.name || tci.id])));
    } catch (err) {
      setErrorMessage(err?.message || "Failed to load readiness.");
    } finally {
      setLoading(false);
    }
  }, [institutionId]);

  useEffect(() => {
    load();
  }, [load]);

  const tciRows = useMemo(() => {
    const map = readiness?.tciReadiness || {};
    return Object.entries(map).map(([tciId, metrics]) => {
      const loopMetric = loopHealthByTci[tciId];
      const loopStatus = loopMetric?.loopStatus;
      const healthChip =
        loopStatus && LOOP_HEALTH_STATUS_CHIP[loopStatus]
          ? LOOP_HEALTH_STATUS_CHIP[loopStatus]
          : null;
      return {
        tciId,
        tciName: tciNameById[tciId] || tciId,
        ...metrics,
        loopHealthStatus: healthChip ? loopStatus : null,
        loopHealthChip: healthChip,
      };
    });
  }, [readiness, tciNameById, loopHealthByTci]);

  if (loading) return <Loading page />;

  return (
    <Box>
      <DashboardPageHeader
        title="Institution readiness"
        subtitle="Which TCIs are active, staffed, and running the feedback loop."
      />

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {!readiness && (
        <Alert severity="info">
          Readiness has not been computed yet. It updates when role assignments, annotations, or
          candidates change.
        </Alert>
      )}

      {readiness && (
        <>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Typography variant="h4">{readiness.overallReadiness ?? 0}%</Typography>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Overall readiness — {readinessStripLabel(readiness.overallReadiness ?? 0)}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={readiness.overallReadiness ?? 0}
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Updated{" "}
                {readiness.lastUpdatedDate
                  ? formatDistanceToNow(readiness.lastUpdatedDate, { addSuffix: true })
                  : "recently"}
              </Typography>
            </Stack>
          </Paper>

          {quarterlySummary && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Latest quarterly loop summary ({quarterlySummary.periodKey})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {quarterlySummary.totalAnnotations ?? 0} annotations ·{" "}
                {quarterlySummary.totalPromotions ?? 0} promotions ·{" "}
                {FDE_ROLE_LABELS["fde-c"]}{" "}
                {quarterlySummary.fdeRoleFill?.fdeC ?? 0} ·{" "}
                {FDE_ROLE_LABELS["fde-t"]}{" "}
                {quarterlySummary.fdeRoleFill?.fdeT ?? 0}
              </Typography>
            </Paper>
          )}

          <Paper>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>TCI</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Loop</TableCell>
                  <TableCell>Roles</TableCell>
                  <TableCell>Pending</TableCell>
                  <TableCell>Quiet {FDE_ROLE_LABELS["fde-c"]}s</TableCell>
                  <TableCell>Last promotion</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tciRows.map((row) => (
                  <React.Fragment key={row.tciId}>
                    <TableRow
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() =>
                        setExpandedTci((prev) => (prev === row.tciId ? null : row.tciId))
                      }
                    >
                      <TableCell>{row.tciName}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={READINESS_STATUS_COLORS[row.status] || "default"}
                          label={row.status || "unknown"}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {row.loopHealthChip ? (
                            <Chip
                              size="small"
                              color={row.loopHealthChip.color}
                              label={row.loopHealthChip.label}
                            />
                          ) : null}
                          <Chip
                            size="small"
                            color={row.loopOperating ? "success" : "warning"}
                            label={
                              row.loopHealthChip
                                ? row.loopOperating
                                  ? "Operating"
                                  : "Idle"
                                : row.loopOperating
                                  ? "Operating"
                                  : "Needs attention"
                            }
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block">
                          {FDE_ROLE_LABELS["fde-c"]} {row.hasFdec ? "✓" : "—"} · Mgr{" "}
                          {row.hasFdeDc ? "✓" : "—"} · {FDE_ROLE_LABELS["fde-t"]}{" "}
                          {row.hasFdeT ? "✓" : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.pendingAnnotations ?? 0} ann / {row.pendingCandidates ?? 0} cand
                      </TableCell>
                      <TableCell>{row.fdecQuietCount ?? 0}</TableCell>
                      <TableCell>
                        {row.lastPromotionAt?.toDate
                          ? format(row.lastPromotionAt.toDate(), "MMM d, yyyy")
                          : "—"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 0, borderBottom: 0 }}>
                        <Collapse in={expandedTci === row.tciId}>
                          <Box sx={{ py: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Training complete: {row.trainingComplete ? "Yes" : "No"}
                            </Typography>
                            <Typography
                              component={RouterLink}
                              to={`/dashboard/tci-governance/${row.tciId}`}
                              variant="body2"
                              color="primary"
                            >
                              Open governance profile
                            </Typography>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
                {!tciRows.length && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography color="text.secondary" variant="body2">
                        No TCI readiness rows yet. Configure domains and governance profiles first.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Box>
  );
};

function ReadinessDashboard() {
  return (
    <RequireLauncherApp appId="loopmanager">
      <ReadinessDashboardContent />
    </RequireLauncherApp>
  );
}

export default ReadinessDashboard;
